/**
 * GovernanceUsageDashboardPage.jsx
 * P2-A: Usage & Quotas dashboard (SuperAdmin — todos os tenants)
 *
 * Backend endpoints:
 *   GET /api/control-plane/usage/all?month=YYYY-MM  → dados agregados (SuperAdmin)
 *   GET /api/billing/usage-summary?month=YYYY-MM    → tenant individual (via billing)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AZURE } from '../../services/api';
import PageHeader from '../../components/layout/PageHeader';
import { COLORS } from '../../components/governance/governanceTheme';
import {
  BarChart2, Users, Activity, DollarSign, AlertTriangle,
  RefreshCw, ChevronDown, ChevronUp, Calendar,
} from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const USAGE_LABELS = {
  pt: {
    pageTitle: 'Usage & Quotas',
    pageSubtitle: 'Consumo agregado por tenant — todos os planos e recursos',
    refresh: (loading) => loading ? 'Carregando…' : 'Atualizar',
    searchPlaceholder: 'Buscar tenant…',
    errorTitle: 'Erro ao carregar dados',
    summaryCards: ['Tenants ativos', 'Total de eventos', 'Custo total estimado', 'Recursos monitorados'],
    summaryCardSubs: [(n, total) => `de ${total} total`, (m) => `mês ${m}`, 'baseado em quota policies', 'tipos de recurso únicos'],
    tableTitle: 'Consumo por Tenant',
    tableHeaders: ['Tenant', 'Status', 'Plano', 'Eventos', 'Custo est.'],
    breakdown: 'Breakdown por recurso',
    emptyMsg: (m) => `Nenhum dado de consumo para ${m}`,
  },
  en: {
    pageTitle: 'Usage & Quotas',
    pageSubtitle: 'Aggregated consumption per tenant — all plans and resources',
    refresh: (loading) => loading ? 'Loading…' : 'Refresh',
    searchPlaceholder: 'Search tenant…',
    errorTitle: 'Error loading data',
    summaryCards: ['Active tenants', 'Total events', 'Estimated total cost', 'Monitored resources'],
    summaryCardSubs: [(n, total) => `of ${total} total`, (m) => `month ${m}`, 'based on quota policies', 'unique resource types'],
    tableTitle: 'Consumption by Tenant',
    tableHeaders: ['Tenant', 'Status', 'Plan', 'Events', 'Est. cost'],
    breakdown: 'Resource breakdown',
    emptyMsg: (m) => `No consumption data for ${m}`,
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtNum = (n) => (n == null ? '–' : Number(n).toLocaleString('pt-BR'));

const STATUS_COLORS = {
  Active:    { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
  Suspended: { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
  Cancelled: { bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Cancelled;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.text }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {status || 'Sem plano'}
    </span>
  );
};

// ─── Summary Card ─────────────────────────────────────────────────────────────

const SummaryCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
    <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: color + '18' }}>
      <Icon size={22} style={{ color }} />
    </div>
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-extrabold mt-0.5" style={{ color: COLORS.primary }}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Tenant Row ───────────────────────────────────────────────────────────────

const TenantRow = ({ tenant, t }) => {
  const [open, setOpen] = useState(false);
  const breakdown = tenant.usage_breakdown || {};
  const keys = Object.keys(breakdown);

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{tenant.tenant_name || `Tenant #${tenant.tenant_id}`}</td>
        <td className="px-4 py-3"><StatusBadge status={tenant.subscription_status} /></td>
        <td className="px-4 py-3 text-sm text-gray-700">{tenant.plan_name || '–'}</td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700 text-right">{fmtNum(tenant.total_events)}</td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700 text-right">
          {tenant.total_cost != null ? `R$ ${Number(tenant.total_cost).toFixed(2)}` : '–'}
        </td>
        <td className="px-4 py-3 text-center text-gray-400">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {open && keys.length > 0 && (
        <tr>
          <td colSpan={6} className="px-8 py-3 bg-gray-50 border-b border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">{t ? t.breakdown : 'Breakdown por recurso'}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {keys.map(k => {
                const b = breakdown[k];
                const pct = b.quota && b.quota > 0 ? Math.min(100, Math.round((b.used / b.quota) * 100)) : null;
                return (
                  <div key={k} className="bg-white rounded-lg border border-gray-200 p-3">
                    <p className="text-xs font-bold text-gray-700 mb-1">{k}</p>
                    <p className="text-sm font-mono text-gray-900">{fmtNum(b.used)} <span className="text-gray-400 font-normal">/{b.quota ? fmtNum(b.quota) : '∞'}</span></p>
                    {pct != null && (
                      <div className="mt-1.5 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981' }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const GovernanceUsageDashboardPage = () => {
  const { lang } = useGovernanceLang();
  const t = USAGE_LABELS[lang] || USAGE_LABELS.pt;
  const [month, setMonth] = useState(currentMonthStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AZURE.get('/control-plane/usage/all', { params: { month } });
      setData(res.data?.data || res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar dados de usage');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const tenants = (data?.tenants || []).filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.tenant_name || '').toLowerCase().includes(q) || String(t.tenant_id).includes(q);
  });

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <PageHeader
        title={t.pageTitle}
        subtitle={t.pageSubtitle}
        icon={BarChart2}
      />

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6 space-y-6">

        {/* ── Controls ── */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="month"
              value={month}
              onChange={e => setMonth(e.target.value)}
              className="text-sm text-gray-700 outline-none bg-transparent"
            />
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            style={{ backgroundColor: COLORS.primary, opacity: loading ? 0.6 : 1 }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t.refresh(loading)}
          </button>
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ml-auto px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none w-52"
          />
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-red-500" />
            <div>
              <p className="font-semibold">{t.errorTitle}</p>
              <p className="text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* ── Summary Cards ── */}
        {!loading && !error && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard icon={Users} label={t.summaryCards[0]} value={fmtNum(summary.active_tenants ?? data?.tenants?.length)} sub={t.summaryCardSubs[0](null, fmtNum(data?.tenants?.length))} color="#064e3b" />
            <SummaryCard icon={Activity} label={t.summaryCards[1]} value={fmtNum(summary.total_events)} sub={t.summaryCardSubs[1](month)} color="#3b82f6" />
            <SummaryCard icon={DollarSign} label={t.summaryCards[2]} value={summary.total_cost != null ? `R$ ${Number(summary.total_cost).toFixed(2)}` : '–'} sub={t.summaryCardSubs[2]} color="#b48c2c" />
            <SummaryCard icon={BarChart2} label={t.summaryCards[3]} value={fmtNum(summary.resource_types)} sub={t.summaryCardSubs[3]} color="#7c3aed" />
          </div>
        )}

        {/* ── Skeleton ── */}
        {loading && (
          <div className="space-y-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white h-14 rounded-xl border border-gray-200 animate-pulse" />
            ))}
          </div>
        )}

        {/* ── Tenants Table ── */}
        {!loading && !error && tenants.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-800">{t.tableTitle}</h2>
              <span className="text-xs text-gray-400">{tenants.length} tenant{tenants.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.tableHeaders[0]}</th>
                    <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.tableHeaders[1]}</th>
                    <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.tableHeaders[2]}</th>
                    <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">{t.tableHeaders[3]}</th>
                    <th className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">{t.tableHeaders[4]}</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tenants.map(tenant => (
                    <TenantRow key={tenant.tenant_id} tenant={tenant} t={t} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && tenants.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <BarChart2 size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">{t.emptyMsg(month)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernanceUsageDashboardPage;
